import {SocketController} from './SocketController'
import {SocketActionController} from './SocketActionController'
import {SocketLobbyListen} from './SocketLobbyListen'
import {SocketGameListen} from './SocketGameListen'
import {SocketMatchmakingListen} from './SocketMatchmakingListen'
import {UIAPIEndpoints} from '../network/UIAPIEndpoints'
import {SocketMonitor} from './SocketMonitor'
import {RoomProtocolMapperClient} from '../RoomProtocolMapperClient.ts'
import {NetworkEnums} from '../../../../shared/GameEnums.ts'
import {ClientMessageSubType} from '@colonist/types'
import {ProtocolClient} from '../ProtocolClient.ts'
import {CA} from '../analytics/Analytics.ts'
import {RoomCommand, RoomEvent} from '@colonist/protocol-lobby'

export class Socket {
    readonly controller: SocketController
    readonly actionController: SocketActionController

    readonly lobbyListener: SocketLobbyListen
    readonly gameListener: SocketGameListen
    readonly matchmakingListener: SocketMatchmakingListen

    readonly monitor: SocketMonitor

    private readonly roomProtocolClient: ProtocolClient<RoomEvent, RoomCommand>
    readonly roomClient: RoomProtocolMapperClient

    constructor(socketActionController: SocketActionController) {
        this.actionController = socketActionController
        this.controller = new SocketController(UIAPIEndpoints.socketServerWSS())

        this.gameListener = new SocketGameListen(this.controller, this.actionController)
        this.lobbyListener = new SocketLobbyListen(this.controller, this.actionController)
        this.matchmakingListener = new SocketMatchmakingListen(this.controller, this.actionController)
        this.monitor = new SocketMonitor(this.controller)

        this.controller.setStateHandler(NetworkEnums.State.RoomEvent, (event: RoomEvent) => {
            this.roomProtocolClient.emitEvent(event.type, event)
        })

        function getAnalytic(command: RoomCommand): [CA.Label, () => any] | undefined {
            switch(command.type) {
                case 'setReadyToStart': return [CA.Label.ClickedRoomReadyToStart, () => undefined]
                case 'selectColor': return undefined
                case 'startGame': return [CA.Label.ClickedRoomStartGame, () => undefined]
                case 'kickPlayer': return [CA.Label.ClickedRoomKickPlayer, () => undefined]
                case 'addBot': return [CA.Label.ClickedRoomAddBot, () => undefined]
                case 'setPrivateGame': return [CA.Label.ClickedRoomSettingChangePrivateGame, () => Number(command.privateGame)]
                case 'toggleNoTrolls': return [CA.Label.ClickedRoomSettingChangeNoTrollsSetting, () => undefined]
                case 'setGameMode': return [CA.Label.ClickedRoomSettingUpdateGameModeSetting, () => command.gameMode]
                case 'setMap': return [CA.Label.ClickedRoomSettingUpdateMapSetting, () => command.map]
                case 'setDiceType': return [CA.Label.ClickedRoomSettingUpdateDiceSetting, () => command.diceType]
                case 'setVictoryPointsToWin': return [CA.Label.ClickedRoomSettingChangeVictoryPointsToWin, () => command.victoryPoints]
                case 'setCardDiscardLimit': return [CA.Label.ClickedRoomSettingChangeCardDiscardLimit, () => command.limit]
                case 'setFriendlyRobber': return [CA.Label.ClickedRoomSettingChangeFriendlyRobber, () => Number(command.friendlyRobber)]
                case 'setMaxPlayers': return [CA.Label.ClickedRoomSettingChangeMaxPlayers, () => command.maxPlayers]
                case 'setHiddenBankCards': return [CA.Label.ClickedRoomSettingChangeHideBankCards, () => Number(command.hiddenBankCards)]
                case 'setGameSpeed': return [CA.Label.ClickedRoomSettingChangeGameSpeed, () => command.gameSpeed]
                case 'setBotSpeed': return [CA.Label.ClickedRoomSettingChangeBotSpeed, () => command.botSpeed]
                case 'startPlayOrderSelection': return undefined
                case 'submitChatText': return undefined
                case 'leave': return [CA.Label.ClickedRoomExit, () => undefined]
                case 'setBotDifficulty': return undefined
                case 'startSeedOrderSelection': return [CA.Label.ClickedRoomCustomPlayOrderSelection, () => undefined]
                case 'setSeedOrder': return undefined
                case 'setPlayOrder': return undefined
                case 'togglePlayOrderSelection': return [CA.Label.ClickedRoomSettingChangePlayOrderSelection, () => undefined]
            }
        }

        const sendRoomCommandToServer = (command: RoomCommand) => {
            const analytic = getAnalytic(command)
            if(analytic != undefined) CA.sendGoogleAnalyticsEvent(CA.Category.Room, analytic[0], analytic[1]())

            this.controller.sender.sendToServerType(ClientMessageSubType.RoomCommand, 'lobby', command)
        }

        this.roomProtocolClient = new ProtocolClient(sendRoomCommandToServer)

        this.roomClient = new RoomProtocolMapperClient(this.roomProtocolClient)
    }

    connect() {
        this.controller.enableConnect()
    }
}
